import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useOrg } from '../lib/org';
import { acceptInvite } from '../api/organizations';
import { Spinner, ErrorAlert } from '../components/ui';
import { cn, ui } from '../lib/ui';

export default function AcceptInvite() {
  const { orgSlug } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { switchOrg } = useOrg();

  const token = params.get('token');
  const orgId = params.get('orgId');

  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  const doAccept = useCallback(async () => {
    if (!token || !orgId) {
      setError('Invalid invitation link. Please check the link and try again.');
      setStatus('error');
      return;
    }
    setStatus('accepting');
    try {
      await acceptInvite(orgId, token);
      setStatus('success');
      await switchOrg(orgId);
      setTimeout(() => navigate('/'), 1500);
    } catch (err) {
      setError(err.message || 'Failed to accept invitation');
      setStatus('error');
    }
  }, [token, orgId, switchOrg, navigate]);

  useEffect(() => {
    if (user && status === 'idle') {
      doAccept();
    }
  }, [user, status, doAccept]);

  if (!user) {
    const returnPath = `/o/${encodeURIComponent(orgSlug)}/invite?token=${encodeURIComponent(token || '')}&orgId=${encodeURIComponent(orgId || '')}`;
    return (
      <div className="mx-auto my-16 max-w-md px-4">
        <div className={cn(ui.card, 'text-center')}>
          <h1 className={cn(ui.h2, 'mb-4')}>Organization invitation</h1>
          <p className={cn(ui.muted, 'mb-4')}>
            You have been invited to join an organization. Please sign in or create an account to
            accept.
          </p>
          <div className="flex justify-center gap-3">
            <Link to="/login" state={{ from: returnPath }} className={ui.btnPrimary}>
              Sign in
            </Link>
            <Link to="/register" state={{ from: returnPath }} className={ui.btnSecondary}>
              Create account
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto my-16 max-w-md px-4">
      <div className={cn(ui.card, 'text-center')}>
        <h1 className={cn(ui.h2, 'mb-4')}>
          {status === 'success' ? 'Welcome!' : 'Accepting invitation...'}
        </h1>

        {status === 'accepting' && <Spinner />}

        {status === 'success' && (
          <p className="text-dcon-success">
            You have joined the organization. Redirecting to your dashboard...
          </p>
        )}

        {status === 'error' && (
          <>
            <ErrorAlert message={error} />
            <div className="mt-4">
              <Link to="/" className={ui.btnSecondary}>
                Go to dashboard
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
